import * as React from "react";

import type { PaginationProps } from "./pagination.types";
import { PAGE_SIZE_OPTIONS } from "./pagination.constants";
import { MultiSelectDropdown } from "./multi-select-dropdown";

import { cn } from "../../lib/utils";

const defaultPageLabel = (currentPage: number, totalPages: number) =>
  `Page ${currentPage} of ${totalPages}`;

const Pagination = ({
  pageSize,
  onPageSizeChange,
  pageSizeLabel,
  currentPage,
  totalPages,
  onPageChange,
  className,
  disabled = false,
  pageLabel = defaultPageLabel,
  previousLabel = "Previous page",
  nextLabel = "Next page",
  iconClassName,
  previousIconClassName,
  nextIconClassName,
}: PaginationProps) => {
  const hasPageSize = pageSize !== undefined && onPageSizeChange !== undefined;
  const safeTotal = Math.max(totalPages, 0);
  const clampedCurrent = safeTotal
    ? Math.min(Math.max(currentPage, 1), safeTotal)
    : 1;
  const canGoPrev = clampedCurrent > 1;
  const canGoNext = safeTotal > 0 && clampedCurrent < safeTotal;

  return (
    <div
      className={cn(
        "w-full inline-flex items-center justify-between gap-3 px-3 py-2",
        className,
      )}
      role="navigation"
      aria-label="Pagination"
    >
      {pageSize !== undefined && onPageSizeChange && (
        <div className="flex min-w-0 items-center gap-2 text-xs text-[#344054]">
          <span className="hidden truncate sm:inline">{pageSizeLabel}</span>
          <div className="w-16 shrink-0">
            <MultiSelectDropdown
              singleSelect
              triggerClassName="h-9 py-1.5"
              side="top"
              disabled={disabled}
              ariaLabel={pageSizeLabel}
              options={PAGE_SIZE_OPTIONS}
              selected={[String(pageSize)]}
              onToggle={(value) => onPageSizeChange(Number(value))}
              placeholder={pageSizeLabel ?? ""}
              searchPlaceholder=""
              emptyMessage=""
              selectedCountLabel=""
              noResultsMessage=""
              noJobsLabel=""
            />
          </div>
        </div>
      )}
      <div
        className={cn(
          "flex items-center justify-between gap-2",
          hasPageSize ? "shrink-0" : "w-full",
        )}
      >
        <button
          type="button"
          className="h-9 rounded-lg border border-black bg-white px-2.5 py-2 text-[#101828] shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] transition-all duration-150 active:translate-y-[2px] disabled:opacity-40 disabled:translate-y-0"
          aria-label={previousLabel}
          disabled={!canGoPrev || disabled}
          onClick={() => onPageChange(clampedCurrent - 1)}
        >
          <ArrowLeftIcon
            className={cn(
              "h-[14px] w-[14px]",
              iconClassName,
              previousIconClassName,
            )}
          />
        </button>
        <span
          className={cn(
            "overflow-hidden text-ellipsis whitespace-nowrap text-center text-[13px] font-semibold tabular-nums text-[#344054]",
            hasPageSize ? "min-w-[11ch]" : "w-[17ch] shrink-0",
          )}
        >
          {pageLabel(clampedCurrent, safeTotal)}
        </span>
        <button
          type="button"
          className="h-9 rounded-lg border border-black bg-white px-2.5 py-2 text-[#101828] shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] transition-all duration-150 active:translate-y-[2px] disabled:opacity-40 disabled:translate-y-0"
          aria-label={nextLabel}
          disabled={!canGoNext || disabled}
          onClick={() => onPageChange(clampedCurrent + 1)}
        >
          <ArrowRightIcon
            className={cn(
              "h-[14px] w-[14px]",
              iconClassName,
              nextIconClassName,
            )}
          />
        </button>
      </div>
    </div>
  );
};

const ArrowLeftIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M12.5 6.66659H0.833344M0.833344 6.66659L6.66668 12.4999M0.833344 6.66659L6.66668 0.833252"
      stroke="currentColor"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRightIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M0.833328 6.66659H12.5M12.5 6.66659L6.66666 0.833252M12.5 6.66659L6.66666 12.4999"
      stroke="currentColor"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export { Pagination };
export type { PaginationProps };
